import { useEffect, useMemo, useState } from "react";
import { Activity, BadgeCheck, BrainCircuit, Check, Cpu, ExternalLink, Gauge, Save, ShieldCheck, Sparkles, TestTube2, Zap } from "lucide-react";
import { AI_MODEL_OPTIONS, DEFAULT_AI_MODEL } from "../../shared/ai-models";
import type { AiModelId, AiModelTestResult } from "../../shared/types";
import { Badge, Button, Card, SectionHeader, Spinner } from "../components/Ui";
import { api } from "../lib/api";
import { useData } from "../lib/data";

const workflowScope = [
  ["基金一页纸", "内部资料 RAG 综合与基金尽调增量结论"],
  ["投资委员会", "委员初审、交叉质询、观点修订与主席汇总"],
  ["公司与行业研究", "公开证据整理、公司底稿与行业映射"],
  ["宏观与组合", "宏观情景解读、组合备忘录与投资记忆复盘"],
];

function formatUpdatedAt(value: string) {
  if (!value) return "跟随部署默认值";
  const normalized = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(date);
}

export function ModelSettingsPage() {
  const { data, refresh } = useData();
  const current = data?.aiModel?.model ?? DEFAULT_AI_MODEL;
  const [selected, setSelected] = useState<AiModelId>(current);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [testResult, setTestResult] = useState<AiModelTestResult | null>(null);

  useEffect(() => setSelected(current), [current]);

  const selectedOption = useMemo(() => AI_MODEL_OPTIONS.find((item) => item.id === selected)!, [selected]);
  const dirty = selected !== current;

  const choose = (model: AiModelId) => {
    setSelected(model);
    setSaved(false);
    setError("");
    setTestResult(null);
  };

  const test = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      setTestResult(await api.testAiModel(selected));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "模型连通性测试失败");
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await api.saveAiModel(selected);
      await refresh();
      setSaved(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "模型设置保存失败");
    } finally {
      setSaving(false);
    }
  };

  return <div className="page-stack model-settings-page">
    <div className="page-title">
      <div><span className="eyebrow">AI MODEL CONTROL</span><h1>AI 模型设置</h1><p>在不暴露 API Key 的前提下，为全站研究任务统一选择 DeepSeek V4 模型。</p></div>
      <Badge tone={current === "deepseek-v4-pro" ? "up" : "dark"}><Cpu size={14}/>当前 · {current === "deepseek-v4-pro" ? "V4 Pro" : "V4 Flash"}</Badge>
    </div>

    <Card className="model-status-hero">
      <div className="model-status-orbit"><span><BrainCircuit size={25}/></span><i/><i/><i/></div>
      <div><span>全站生效模型</span><strong>{data?.aiModel?.model || DEFAULT_AI_MODEL}</strong><p>新发起的 AI 研究、投委会与内部资料分析都会使用该模型；纯公开数据行情与规则计算不消耗模型。</p></div>
      <div className="model-status-meta"><span>配置来源<strong>{data?.aiModel?.source === "workspace" ? "工作区设置" : "部署默认值"}</strong></span><span>最近更新<strong>{formatUpdatedAt(data?.aiModel?.updatedAt || "")}</strong></span></div>
    </Card>

    <div className="model-settings-layout">
      <Card className="model-selector-card">
        <SectionHeader title="选择模型" subtitle="Flash 适合高频工作流，Pro 适合复杂推理；切换不会改写历史报告。" action={<a className="text-link" href="https://api-docs.deepseek.com/zh-cn/quick_start/pricing" target="_blank" rel="noreferrer">官方价格 <ExternalLink size={13}/></a>}/>
        <div className="model-option-grid">
          {AI_MODEL_OPTIONS.map((option) => {
            const active = selected === option.id;
            const isPro = option.id === "deepseek-v4-pro";
            return <button type="button" key={option.id} className={active ? "model-option active" : "model-option"} onClick={() => choose(option.id)} aria-pressed={active}>
              <header><span className={isPro ? "pro" : "flash"}>{isPro ? <Sparkles size={18}/> : <Zap size={18}/>}</span><div><strong>{option.name}</strong><small>{option.positioning}</small></div>{active && <i><Check size={14}/></i>}</header>
              <p>{option.bestFor}</p>
              <div className="model-price-row"><span>输入 ¥{option.inputPrice}<small>/ 百万 tokens</small></span><span>输出 ¥{option.outputPrice}<small>/ 百万 tokens</small></span><span>并发 {option.concurrency}<small>官方上限</small></span></div>
              <footer>{isPro ? "更强复杂任务能力 · 成本约为 Flash 3 倍" : "低延迟与低成本 · 当前生产默认"}</footer>
            </button>;
          })}
        </div>
        <div className={selected === "deepseek-v4-pro" ? "model-cost-note warning" : "model-cost-note"}><ShieldCheck size={17}/><div><strong>{selected === "deepseek-v4-pro" ? "Pro 成本提示" : "Flash 运行提示"}</strong><span>{selected === "deepseek-v4-pro" ? "按官方定价，缓存未命中输入和输出价格均约为 Flash 的 3 倍。适合演示重点任务和深度研究。" : "适合保持全站高频运行。遇到复杂投委会或深度底稿时，可回到这里切换 Pro。"}</span></div></div>
      </Card>

      <Card className="model-scope-card">
        <SectionHeader title="生效范围" subtitle="后端在每次模型调用前读取这一设置。"/>
        <div className="model-scope-list">{workflowScope.map(([title, detail], index) => <article key={title}><b>0{index + 1}</b><div><strong>{title}</strong><span>{detail}</span></div><Check size={14}/></article>)}</div>
        <div className="model-boundary"><Gauge size={17}/><div><strong>不受影响的模块</strong><span>行情抓取、基金公开净值、组合数学计算、RAG 检索与历史已生成报告不会被模型设置改写。</span></div></div>
      </Card>
    </div>

    <Card className="model-connection-card">
      <div><TestTube2 size={19}/><span>上线前检查</span><strong>测试 {selectedOption.name} 的账户权限与接口连通性</strong><small>测试只发送一句最小提示，不保存研究内容，也不会自动改变当前模型。</small></div>
      <Button className="secondary" type="button" onClick={() => void test()} disabled={testing || saving}>{testing ? <Spinner label="正在测试"/> : <><Activity size={15}/>测试连接</>}</Button>
      {testResult && <div className="model-test-result"><BadgeCheck size={17}/><span><strong>连接正常</strong><small>{testResult.model} · {testResult.latencyMs}ms · {testResult.message}</small></span></div>}
    </Card>

    {error && <div className="form-error">{error}</div>}
    <div className="model-savebar"><div><strong>{saved ? "模型设置已生效" : dirty ? `准备切换至 ${selectedOption.name}` : "当前选择没有变化"}</strong><span>{dirty ? "保存后，新发起的全站 AI 任务立即使用新模型。" : "API Key 仍由 Cloudflare Secret 管理，不会显示在页面中。"}</span></div><Button type="button" onClick={() => void save()} disabled={saving || !dirty}>{saving ? <Spinner label="正在保存"/> : <><Save size={16}/>{saved ? "已保存" : "保存并应用"}</>}</Button></div>
  </div>;
}
